/**
 * hh-image-gallery
 *
 * Turns the drag/scroll image strip into an accessible carousel.
 *
 * The wrapper element provides role="region" + aria-label and the
 * screen-reader instructions in the section markup.
 */
class HHImageGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-image-gallery-track]')
    this.slides = Array.from(
      this.querySelectorAll('[data-image-gallery-slide]')
    )
    this.controls = this.querySelector('[data-image-gallery-controls]')
    this.prevButton = this.querySelector('[data-image-gallery-prev]')
    this.nextButton = this.querySelector('[data-image-gallery-next]')

    if (!this.track || this.slides.length === 0) return

    this.onPrev = () => this.scrollBySlide(-1)
    this.onNext = () => this.scrollBySlide(1)
    this.onScroll = () => this.updateControls()

    this.prevButton?.addEventListener('click', this.onPrev)
    this.nextButton?.addEventListener('click', this.onNext)
    this.track.addEventListener('scroll', this.onScroll, { passive: true })

    // Toggle visibility state as slides scroll in and out of the track.
    this.observer = new IntersectionObserver(
      (entries) => this.onIntersect(entries),
      { root: this.track, threshold: 0.5 }
    )
    this.slides.forEach((slide) => this.observer.observe(slide))

    this.updateControls()
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener('click', this.onPrev)
    this.nextButton?.removeEventListener('click', this.onNext)
    this.track?.removeEventListener('scroll', this.onScroll)
    this.observer?.disconnect()
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      const visible = entry.isIntersecting && entry.intersectionRatio >= 0.5
      this.setSlideVisibility(entry.target, visible)
    })
    this.updateControls()
  }

  setSlideVisibility(slide, visible) {
    slide.setAttribute('aria-hidden', visible ? 'false' : 'true')
    this.setFocusable(slide, visible)

    slide
      .querySelectorAll('a, button, input, select, textarea, [tabindex]')
      .forEach((el) => this.setFocusable(el, visible))
  }

  // Remove an element from the tab order while hidden, remembering its original
  // tabindex so it can be restored exactly when the slide becomes visible again.
  setFocusable(el, visible) {
    if (visible) {
      if (!el.hasAttribute('data-hh-prev-tabindex')) return
      const prev = el.getAttribute('data-hh-prev-tabindex')
      if (prev === '') {
        el.removeAttribute('tabindex')
      } else {
        el.setAttribute('tabindex', prev)
      }
      el.removeAttribute('data-hh-prev-tabindex')
    } else {
      if (!el.hasAttribute('data-hh-prev-tabindex')) {
        el.setAttribute(
          'data-hh-prev-tabindex',
          el.getAttribute('tabindex') ?? ''
        )
      }
      el.setAttribute('tabindex', '-1')
    }
  }

  scrollBySlide(direction) {
    const first = this.slides[0]
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0
    const amount = first.getBoundingClientRect().width + gap
    this.track.scrollBy({ left: direction * amount, behavior: 'smooth' })
  }

  updateControls() {
    if (!this.controls) return

    const maxScroll = this.track.scrollWidth - this.track.clientWidth
    const scrollable = maxScroll > 1
    this.controls.hidden = !scrollable

    if (!scrollable) return

    const position = this.track.scrollLeft
    if (this.prevButton) this.prevButton.disabled = position <= 1
    if (this.nextButton) this.nextButton.disabled = position >= maxScroll - 1
  }
}

if (!customElements.get('hh-image-gallery')) {
  customElements.define('hh-image-gallery', HHImageGallery)
}
